import { useState, useEffect } from 'react';
import ModalBase from '@/components/base/ModalBase';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type { Contest, ContestType, ContestStatus } from '@/mocks/adminContests';
import { contestTypeLabels, contestTypeIcons, contestTypeColors, contestStatusLabels } from '@/mocks/adminContests';

interface ContestFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (contest: Partial<Contest>) => void;
  contest?: Contest | null;
}

const types: ContestType[] = ['sorteo', 'trivia', 'comentarios', 'compartir', 'participacion'];
const statuses: ContestStatus[] = ['activo', 'proximamente', 'pausado', 'finalizado'];

export default function ContestFormModal({ open, onClose, onSave, contest }: ContestFormModalProps) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    prize: '',
    startDate: '',
    endDate: '',
    rules: '',
    type: 'sorteo' as ContestType,
    status: 'activo' as ContestStatus,
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (contest) {
      setForm({
        title: contest.title,
        description: contest.description,
        prize: contest.prize,
        startDate: contest.startDate,
        endDate: contest.endDate,
        rules: contest.rules,
        type: contest.type,
        status: contest.status,
      });
    } else {
      const today = new Date().toISOString().slice(0, 10);
      const nextWeek = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
      setForm({
        title: '',
        description: '',
        prize: '',
        startDate: today,
        endDate: nextWeek,
        rules: '',
        type: 'sorteo',
        status: 'activo',
      });
    }
  }, [contest, open]);

  const titleOk = form.title.trim().length > 0;
  const prizeOk = form.prize.trim().length > 0;
  const datesOk = form.startDate && form.endDate && form.startDate <= form.endDate;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!titleOk) {
      await SwalAlert.warning('Validación', 'El título del concurso es requerido.');
      return;
    }
    if (!prizeOk) {
      await SwalAlert.warning('Validación', 'El premio es requerido.');
      return;
    }
    if (!datesOk) {
      await SwalAlert.warning('Validación', 'La fecha de fin debe ser mayor o igual a la fecha de inicio.');
      return;
    }

    setSaving(true);
    onSave({
      ...form,
      id: contest?.id || Date.now(),
      stationId: contest?.stationId || 1,
      participants: contest?.participants || 0,
    });
    onClose();
    setSaving(false);
    await SwalAlert.toast(contest ? 'Concurso actualizado.' : 'Concurso creado.', 'success');
  };

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title={contest ? 'Editar concurso' : 'Nuevo concurso'}
      subtitle={`Completá los campos para ${contest ? 'modificar' : 'crear'} un concurso`}
      size="lg"
      headerIcon={
        <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
          <i className="ri-trophy-line text-amber-600"></i>
        </div>
      }
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 font-semibold text-sm cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="contest-form"
            disabled={saving}
            className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm cursor-pointer transition-all whitespace-nowrap ${
              titleOk && prizeOk && datesOk
                ? 'bg-primary-500 text-white hover:bg-primary-600'
                : 'bg-primary-200 text-primary-400 cursor-not-allowed'
            }`}
          >
            {saving ? 'Guardando…' : contest ? 'Guardar cambios' : 'Crear concurso'}
          </button>
        </>
      }
    >
      <form id="contest-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-foreground-700 text-xs font-semibold mb-1.5">
            Título del concurso <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className={`w-full px-3 py-2 rounded-lg bg-background-100 border text-foreground-800 text-sm outline-none transition-colors ${
              !titleOk && form.title !== '' ? 'border-red-300 focus:border-red-400' : 'border-background-200 focus:border-primary-300'
            }`}
            placeholder="Ej: Gana Meet & Greet con Los Ecos"
          />
          {!titleOk && form.title !== '' && (
            <p className="text-red-500 text-[10px] mt-1">El título es requerido</p>
          )}
        </div>

        <div>
          <label className="block text-foreground-700 text-xs font-semibold mb-1.5">Descripción</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full px-3 py-2 rounded-lg bg-background-100 border border-background-200 text-foreground-800 text-sm outline-none focus:border-primary-300 transition-colors resize-none"
            rows={3}
            placeholder="Describí el concurso, cómo participar..."
            maxLength={500}
          ></textarea>
          <p className="text-foreground-300 text-[10px] mt-1 text-right">{form.description.length}/500</p>
        </div>

        <div>
          <label className="block text-foreground-700 text-xs font-semibold mb-1.5">
            Premio <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.prize}
            onChange={(e) => setForm({ ...form, prize: e.target.value })}
            className={`w-full px-3 py-2 rounded-lg bg-background-100 border text-foreground-800 text-sm outline-none transition-colors ${
              !prizeOk && form.prize !== '' ? 'border-red-300 focus:border-red-400' : 'border-background-200 focus:border-primary-300'
            }`}
            placeholder="Ej: Tarjeta de regalo de $50"
          />
          {!prizeOk && form.prize !== '' && (
            <p className="text-red-500 text-[10px] mt-1">El premio es requerido</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-foreground-700 text-xs font-semibold mb-1.5">Fecha inicio</label>
            <input
              type="date"
              value={form.startDate}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-background-100 border border-background-200 text-foreground-800 text-sm outline-none focus:border-primary-300 transition-colors"
            />
          </div>
          <div>
            <label className="block text-foreground-700 text-xs font-semibold mb-1.5">Fecha fin</label>
            <input
              type="date"
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              className={`w-full px-3 py-2 rounded-lg bg-background-100 border text-foreground-800 text-sm outline-none transition-colors ${
                form.startDate && form.endDate && !datesOk ? 'border-red-300 focus:border-red-400' : 'border-background-200 focus:border-primary-300'
              }`}
            />
          </div>
        </div>

        {form.startDate && form.endDate && !datesOk && (
          <p className="text-red-500 text-xs">La fecha de fin debe ser posterior a la fecha de inicio</p>
        )}

        <div>
          <label className="block text-foreground-700 text-xs font-semibold mb-1.5">Tipo de concurso</label>
          <div className="grid grid-cols-5 gap-1.5">
            {types.map((t) => {
              const colors = contestTypeColors[t];
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm({ ...form, type: t })}
                  className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded-lg text-[10px] font-semibold cursor-pointer transition-all ${
                    form.type === t
                      ? 'ring-2 ring-offset-1 ring-primary-300 ' + colors.bg + ' ' + colors.text + ' border ' + colors.border
                      : colors.bg + ' ' + colors.text + ' border ' + colors.border + ' opacity-60 hover:opacity-90'
                  }`}
                >
                  <i className={`${contestTypeIcons[t]} text-sm`}></i>
                  {contestTypeLabels[t]}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-foreground-700 text-xs font-semibold mb-1.5">Estado</label>
          <div className="flex items-center gap-2 flex-wrap">
            {statuses.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setForm({ ...form, status: s })}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                  form.status === s
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                }`}
              >
                {contestStatusLabels[s]}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-foreground-700 text-xs font-semibold mb-1.5">Reglas</label>
          <textarea
            value={form.rules}
            onChange={(e) => setForm({ ...form, rules: e.target.value })}
            className="w-full px-3 py-2 rounded-lg bg-background-100 border border-background-200 text-foreground-800 text-sm outline-none focus:border-primary-300 transition-colors resize-none"
            rows={4}
            placeholder="Ej: 1. Ser mayor de 18 años. 2. Una participación por persona..."
            maxLength={500}
          ></textarea>
          <p className="text-foreground-300 text-[10px] mt-1 text-right">{form.rules.length}/500</p>
        </div>
      </form>
    </ModalBase>
  );
}